import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import fscreen from 'fscreen';
import { action, observable, runInAction } from 'mobx';
import type { ResponseBase, TwilioRoomResponse } from 'tm3-core/api.dtos';
import { GetTwilioOnlineRoom, GetTwilioRoom, TwilioReminderRequest } from 'tm3-core/api.dtos';
import { connect } from 'twilio-video';
import { createLocalVideoTrack } from 'twilio-video';

class VideoStore {
  @observable roomId = '';
  @observable roomName = '';
  @observable token = '';
  @observable joined = false;
  @observable hasPartipants = false;
  @observable hasPreview = false;
  @observable isAudioEnabled = true;
  @observable isVideoEnabled = true;
  @observable isFullScreen = false;
  @observable roomCreated = false;
  @observable invalidCode = false;
  @observable callEnded = false;
  @observable videoCode = '';
  @observable startDateTime = 'Unknown';
  @observable endDateTime = '';
  @observable loading = false;
  @observable practitionerName = '';
  @observable smsEnabled = false;
  client = new JsonServiceClient();
  room: any; // the twilio room object
  @observable reminderSent: boolean;
  localTrack: any;

  @action.bound
  async startVideo(value: number | string, isOnline: boolean, joinImmediately: boolean) {
    let req = isOnline
      ? new GetTwilioOnlineRoom({ videoCode: value })
      : new GetTwilioRoom({ appointmentId: value });
    this.loading = true;
    this.hasPreview = false;
    this.reminderSent = false;
    if (value) {
      const res = await this.client.get(req);
      this.processResult(res);
    }
    try {
      if (!this.roomCreated) {
        return;
      }
      if (!joinImmediately) {
        this.enableLocalPreview();
        return;
      }
      this.joined = true;
      let options = {
        video: { width: 1920, height: 1080, frameRate: 24 },
        preferredVideoCodecs: ['VP8', 'H264'], // needed for ios 13.4
        audio: true,
        name: this.roomName
      };

      const room = await connect(this.token, options);
      this.room = room;
      this.enableLocalPreview();
      room.participants.forEach((participant) => this.subscribeParticipant(participant));
      room.on('participantConnected', (participant) => {
        this.subscribeParticipant(participant);
      });

      room.on('participantDisconnected', (participant) => {
        this.unsubscribeParticipant(participant);
      });
      room.on('disconnected', (room) => {
        const tracks = [];
        room.localParticipant.tracks.forEach((publication) => {
          publication.track.stop();
          tracks.push(publication.track);
          const attachedElements = publication.track.detach();
          attachedElements.forEach((element) => element.remove());
        });
        room.localParticipant.unpublishTracks(tracks);
      });
    } catch (err) {
      alert(
        'There was a problem initiating the video connection.\r\n\r\n' +
          'This is most likely an issue with your Webcam or your browser.\r\n\r\n' +
          'Techie code:' +
          err
      );
    }
  }

  @action.bound
  processResult(res: ResponseBase<TwilioRoomResponse>) {
    this.token = res.result.token;
    this.roomId = res.result.roomId;
    this.roomName = res.result.roomName;
    this.roomCreated = res.result.roomCreated;
    this.startDateTime = res.result.startDateTime;
    this.endDateTime = res.result.endDateTime;
    this.practitionerName = res.result.practitionerName;
    this.videoCode = res.result.videoCode;
    this.smsEnabled = res.result.smsEnabled;
    this.loading = false;
  }

  @action.bound
  async enableLocalPreview() {
    if (this.hasPreview) {
      return;
    }
    setTimeout(async () => {
      try {
        if (this.room) {
          try {
            const localVideoTrack = Array.from(this.room.localParticipant.videoTracks.values())[0]
              .track;
            if (localVideoTrack) {
              this.localTrack = localVideoTrack;
            }
          } catch {}
        }
        if (!this.localTrack) {
          const localTrack = await createLocalVideoTrack();
          this.localTrack = localTrack;
        }
        const localMediaContainer = document.getElementById('local-media');
        const video = this.localTrack.attach();
        localMediaContainer.appendChild(video);

        this.hasPreview = true;
      } catch {}
    }, 1000);
  }

  @action.bound
  subscribeParticipant(participant) {
    participant.tracks.forEach((publication) => {
      if (publication.isSubscribed) {
        const track = publication.track;
        document.getElementById('remote-media').appendChild(track.attach());
        this.hasPartipants = true;
      }
    });
    participant.on('trackUnsubscribed', (track) => {
      track.detach().forEach((element) => element.remove());
      this.hasPartipants = false;
    });
    participant.on('trackSubscribed', (track) => {
      document.getElementById('remote-media').appendChild(track.attach());
      this.hasPartipants = true;
    });
  }

  @action.bound
  unsubscribeParticipant(participant) {
    this.hasPartipants = false;
    participant.tracks.forEach((publication) => {
      if (publication.track) {
        const attachedElements = publication.track.detach();
        attachedElements.forEach((element) => element.remove());
      }
    });
  }

  @action.bound
  hangUp() {
    this.joined = false;
    this.callEnded = true;
    try {
      this.room.disconnect();
    } catch (err) {}
    if (this.localTrack) {
      this.localTrack.stop();
      this.localTrack = null;
    }
  }

  @action.bound
  toggleAudioEnabled() {
    const publications = this.room.localParticipant.audioTracks;
    publications.forEach((audioTrack) =>
      this.isAudioEnabled ? audioTrack.track.disable() : audioTrack.track.enable()
    );

    this.isAudioEnabled = !this.isAudioEnabled;
  }

  @action.bound
  toggleVideoEnabled() {
    const publications = this.room.localParticipant.videoTracks;
    publications.forEach((videoTrack) =>
      this.isVideoEnabled ? videoTrack.track.disable() : videoTrack.track.enable()
    );

    this.isVideoEnabled = !this.isVideoEnabled;
  }

  @action.bound
  toggleFullScreen() {
    const el = document.getElementById('video-elements');

    if (fscreen.fullscreenEnabled) {
      fscreen.addEventListener('fullscreenchange', handler.bind(this), false);
      fscreen.requestFullscreen(el);
    }
    if (this.isFullScreen) {
      fscreen.exitFullscreen();
    }
    this.isFullScreen = !this.isFullScreen;

    function handler() {
      if (fscreen.fullscreenElement !== null) {
        this.isFullScreen = true;
      } else {
        this.isFullScreen = false;
      }
    }
  }

  @action.bound
  async joinNow(appointmentId: number | string, isOnline: boolean) {
    this.joined = true;
    this.hasPreview = false;
    try {
      this.localTrack.detach();
      this.localTrack.stop();
    } catch {}
    this.localTrack = null;
    this.startVideo(appointmentId, isOnline, true);
  }

  @action.bound
  async checkAppointment(videoCode: string) {
    try {
      this.invalidCode = false;
      const req = new GetTwilioOnlineRoom({ videoCode });
      const res = await this.client.get(req);
      this.processResult(res);
    } catch {
      this.invalidCode = true;
    }
  }

  @action.bound
  async sendCodeReminder() {
    const { videoCode } = this;
    if (videoCode) {
      this.client.post(new TwilioReminderRequest({ videoCode }));
    }
    this.reminderSent = true;
  }
}

export default VideoStore;
